<template>
  <div class="history-wrapper d-flex flex-row justify-content-between">
    <div class="history__col-icon">
      <div class="history-icon">
        <span><font-awesome-icon
          :icon="['far', getEventTypeIcon(item.action)]"
          size="lg"
        /></span>
      </div>
    </div>
    <div class="history__col-info d-flex flex-column">
      <div class="d-flex justify-content-between">
        <div class="info">
          <p class="title">
            {{ parseAction(item.action) }} {{ item.userEmail ? `by ${item.userEmail}` : '' }}
          </p>
          <p class="subtitle">
            {{ item.date | dateFormat }} ({{ timezoneEnv }})
          </p>
        </div>
        <div
          v-if="showAction"
          class="history__col-actions d-flex align-items-center"
        >
          <button
            :id="`btnViewOrClose${index}`"
            class="btn btn-lg btn-outline-secondary px-5"
            @click="() => {toggleOpen(item.id, item.opened)}"
          >
            {{ item.opened ? 'Close' : 'View' }}
          </button>
        </div>
      </div>
      <div
        v-if="showAction"
        v-show="item.opened"
        :class="['template-preview']"
      >
        <div class="template-preview__header d-flex justify-content-between align-items-center">
          <h2>Preview</h2>
          <div
            v-if="item.currentVersion"
            class="current_version"
          >
            Current Version
          </div>
          <button
            v-else
            :id="`btnRevert${index}`"
            class="btn btn-lg btn-primary px-5"
            :disabled="item.templateVersionId === revertedFrom"
            @click="openRevertDialog"
          >
            Revert
          </button>
        </div>
        <HistoryPreview
          :iframe-id="`template-iframe-${item.id}`"
          :preview-content="previewContent"
        />
        <BaseDialog
          ref="revertDialog"
          :content="'Reverting the template will update the code to reflect what it was on the date selected. Personalization tags and dynamic content may be removed. Do you still want to revert this template?'"
          :submit-button="revertButton"
        />
        <ejs-toast
          ref="errorToastItem"
          :position="{ X: 'Right', Y: 'Top' }"
          show-close-button="true"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import moment from 'moment'
import HistoryPreview from './HistoryPreview'
import BaseDialog from '@/components/Dialog/BaseDialog'
import { systemTimezone } from '../../../src/constants/general'

export default {
  name: 'HistoryItem',
  components: {
    HistoryPreview,
    BaseDialog
  },
  provide () {
    return {
      hasPreviewBeenOpened: () => this.hasBeenOpened
    }
  },
  props: {
    showAction: {
      type: Boolean,
      required: false,
      default: false
    },
    item: Object,
    index: Number
  },
  data () {
    return {
      historyItem: this.item,
      templateBuilderQuery: this.$helpers.getTemplateBuilderQuery(this.$route),
      previewContent: null,
      timezoneEnv: '',
      revertButton: {
        label: 'Revert',
        class: 'btn-primary',
        action: this.onRevertClick
      }
    }
  },
  computed: {
    ...mapGetters({
      templateSummary: 'getTemplateSummary',
      activeType: 'getActiveType'
    }),
    ...mapState({
      accountId: state => state.templateBuilder.accountId
    }),
    revertedFrom () {
      console.log('this.templateSummary', this.templateSummary)
      return this.templateSummary.version.revertedFrom ?? null
    },
    hasBeenOpened: function () {
      return this.item.hasBeenOpened
    },
    date () {
      if (this.item.date) return moment(moment.utc(this.item.date)).local()
      return null
    }
  },
  mounted () {
    if (this.showAction) {
      this.setPreview(this.item.opened)
    }
    this.timezoneEnv = systemTimezone()
  },
  methods: {
    openRevertDialog () {
      this.$refs.revertDialog.openDialog()
    },
    getEventTypeIcon (eventType) {
      let icon = 'server'
      switch (eventType) {
        case 'Add':
        case 'AddWithCulture':
        case 'ChildAdded':
          icon = 'plus'
          break
        case 'Update':
        case 'NewVersion':
        case 'UpdateCulture':
          icon = 'pen'
          break
        case 'Activate':
          icon = 'check'
          break
        case 'Deactivate':
          icon = 'times'
          break
        case 'Revert':
          icon = 'history'
          break
        case 'Delete':
          icon = 'trash'
          break
        case 'Clone':
          icon = 'clone'
          break
        case 'Cancelled':
          icon = 'times'
          break
        default:
          icon = 'server'
      }
      return icon
    },
    parseAction (eventType) {
      switch (eventType) {
        case 'Add': return 'Created'
        case 'AddWithCulture': return 'New Language Added'
        case 'NewVersion': return 'Updated'
        case 'ChildAdded': return 'Template added to campaign'
        case 'Activate': return 'Activated'
        case 'Deactivate': return 'Deactivated'
        case 'Revert': return 'Reverted'
        case 'Clone': return 'Cloned'
        case 'Delete': return 'Deleted'
        case 'Update': return 'Updated'
        case 'UpdateCulture': return 'Updated Culture'
        case 'Cancelled':
        default: return this.$helpers.camelCaseToCapitalize(eventType)
      }
    },
    toggleOpen (templateHistoryId, isOpen) {
      this.$store.commit('SET_TEMPLATE_HISTORY_ITEM_OPENED', { templateHistoryId, opened: !isOpen })
      this.setPreview(!isOpen)
    },
    onRevertClick () {
      if (this.activeType === 'saved') {
        this.$store.dispatch(
          'revertTemplateVersion',
          { ...this.templateBuilderQuery, templateVersionId: this.item.templateVersionId }
        )
          .then(response => {
            if (this.accountId) {
              const templateId = this.$route.query.templateId
              const userEmail = atob(this.$route.query.userEmail)
              if (templateId) {
                this.$store.dispatch('setTemplateSummary', { templateId, userEmail, showLoading: true })
                this.$store.dispatch('setTemplateHistory', { templateId, userEmail, showLoading: true })
              }
            }
          })
          .catch(error => {
            if (error?.response?.data?.status === 400 || error.status === 400) {
              const message = error.response.data.title ?? error.message
              this.$refs.errorToastItem.show({ content: message, cssClass: 'notification-error' })
            }
            console.log(error)
          })
      } else if (this.activeType === 'prebuilt') {
        this.$store.dispatch(
          'revertPrebuiltTemplateVersion',
          { ...this.templateBuilderQuery, templateVersionId: this.item.templateVersionId }
        )
          .then(response => {
            if (this.accountId) {
              const templateId = this.$route.query.templateId
              const userEmail = atob(this.$route.query.userEmail)
              if (templateId) {
                this.$store.dispatch('setPrebuiltTemplateSummary', { templateId, userEmail, showLoading: true })
                this.$store.dispatch('setPrebuiltTemplateHistory', { templateId, userEmail, showLoading: true })
              }
            }
          })
          .catch(error => {
            if (error?.response?.data?.status === 400 || error.status === 400) {
              const message = error.response.data.title ?? error.message
              this.$refs.errorToastItem.show({ content: message, cssClass: 'notification-error' })
            }
            console.log(error)
          })
      }
    },
    setPreview (isOpen) {
      if (isOpen && this.item.previewContent) {
        this.previewContent = this.item.previewContent
      } else if (isOpen) {
        if (this.activeType === 'saved') {
          this.$store.dispatch('setTemplatePreviewContent', { ...this.templateBuilderQuery, templateId: this.$route.query.templateId, templateVersionId: this.item.templateVersionId, templateHistoryId: this.item.id })
            .then((resp) => {
              this.previewContent = resp
            })
        } else if (this.activeType === 'prebuilt') {
          this.$store.dispatch('setPrebuiltTemplatePreviewContent', { ...this.templateBuilderQuery, templateId: this.$route.query.templateId, templateVersionId: this.item.templateVersionId, templateHistoryId: this.item.id })
            .then((resp) => {
              this.previewContent = resp
            })
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
div.history {
  &-wrapper:last-child {
    .history-icon {
      &:after {
          content: unset;
      }
    }
  }
  .current_version {
    background-color: $matisse;
    color: $white;
    border-radius: 20px;
    padding: 0px 10px;
    font-size: 12px;
    line-height: 12px;
    font-weight: 100;
    letter-spacing: 1px;
    height: 22px;
    display: flex;
    align-items: center;
  }
  &__col-icon {
    flex-basis: 80px;
    .history-icon {
      height: 100%;
      width: 38px;
      position: relative;
      min-height: 70px;
      svg {
        vertical-align: 0;
      }
      &:after {
          content: '';
          border-right: 1px solid #D7D7E1;
          min-height: 100%;
          width: 1px;
          position: absolute;
          left: 50%;
      }

      span {
          background: #E0E0EB;
          margin-top: 6px;
          border-radius: 50px;
          padding: 8px;
          color: #24245A;
          height: 38px;
          width: 38px;
          display: block;
          text-align: center;
      }
    }
  }

  &__col-info {
    flex-basis: calc(100% - 80px);
    .title {
      color: #000;
      margin-bottom: 3px;
      line-height: 21px;
    }

    .subtitle {
      color: #666;
      margin: 0;
    }
    .template-preview {
      margin: 25px 0;
      border: 1px solid #D7D7E1;
      padding: 0;
      height: auto;
      overflow: hidden;
      &__header {
        padding: 10px 15px 10px 30px;
        border-bottom: 1px solid #D7D7E1;
        h2{
          margin: 0;
        }
      }
    }
    .preview-enter-active, .preview-leave-active {
      transition: all 1s ease-in-out;
    }
    .preview-enter, .preview-leave-to {
      margin: 0;
      height: 0;
      border-width: 0;
    }
    .preview-enter-to {
      margin: 25px 0;
      height: auto;
      border-width: 1px;
    }
  }
}
</style>
